// React imports
import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";

// MUI imports
import {Alert, Box, Card, Grid, Snackbar} from "@mui/material";

// MUI Icons imports
import CheckIcon from "@mui/icons-material/Check";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

// Customized MUI components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// Steps imports
import SelectTripStep from "./SelectTripStep";
import TravelerDetailsStep from "./TravelerDetailsStep";
import TripCustomizationStep from "./TripCustomizationStep";
import ReviewPaymentStep, { buildReviewPricing } from "./ReviewPaymentStep";
import OrderSummaryPanel from "./OrderSummaryPanel";
import SendPaymentLinkModal from "components/SendPaymentLinkModal";

// Other imports
import { useNavigate, useParams, Link as RouterLink } from "react-router-dom";
import { useAuth } from "auth/Auth";
import {WIZARD_STEPS, createEmptyTraveler, getTripNights, getOfferWindow,} from "./hardcodedData";
import { previewPrivateObject } from "../../../utils/S3";
import { useScrollContainer } from "shell-v2/ScrollContainer";

const GATEWAY_PAYMENT_METHOD = {
  stripe: "Card",
  razorpay: "UPI",
};

// Stepper Component

const OrderWizardStepper = ({
  steps,
  activeStep,
  completedSteps,
  onStepClick,
  allStepsClickable = false,
}) => (
    <Box sx={{ width: "100%", overflowX: "auto", mt: 2.5, pt: 0.75, pb: 0.25 }}>
      <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            minWidth: { xs: 640, md: "100%" },
            px: 0.5,
          }}
      >
        {steps.map((label, index) => {
          const isCompleted = completedSteps.has(index) && index < activeStep;
          const isActive = activeStep === index;
          const isPast = index < activeStep || isCompleted;
          const isFirst = index === 0;
          const isLast = index === steps.length - 1;
          const lineBeforeBright = !isFirst && index <= activeStep;
          const lineAfterBright = !isLast && isPast;
          const isClickable = allStepsClickable || isCompleted || index <= activeStep;

          return (
              <Box
                  key={label}
                  onClick={() => isClickable && onStepClick?.(index)}
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    minWidth: 56,
                    cursor: isClickable ? "pointer" : "default",
                    opacity: isClickable ? 1 : 0.6,
                  }}
              >
                <Box sx={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Box
                      sx={{
                        flex: 1,
                        height: 2,
                        bgcolor: lineBeforeBright ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.28)",
                        visibility: isFirst ? "hidden" : "visible",
                        mr: isFirst ? 0 : -0.25,
                      }}
                  />
                  <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "0.8rem",
                        fontWeight: 700,
                        flexShrink: 0,
                        zIndex: 2,
                        transition: "all 0.2s ease",
                        boxSizing: "border-box",
                        ...(isCompleted
                            ? { bgcolor: "#1F8A56", color: "#fff", border: "2px solid #1F8A56" }
                            : isActive
                                ? { bgcolor: "#fff", color: "#1565C8", border: "2px solid #fff" }
                                : { bgcolor: "#0E2348", color: "#fff", border: "2px solid rgba(255,255,255,0.65)" }),
                      }}
                  >
                    {isCompleted ? <CheckIcon sx={{ fontSize: 18 }} /> : index + 1}
                  </Box>
                  <Box
                      sx={{
                        flex: 1,
                        height: 2,
                        bgcolor: lineAfterBright ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.28)",
                        visibility: isLast ? "hidden" : "visible",
                        ml: isLast ? 0 : -0.25,
                      }}
                  />
                </Box>
                <MDTypography
                    variant="caption"
                    sx={{
                      mt: 1,
                      textAlign: "center",
                      fontSize: "0.62rem",
                      fontWeight: isActive ? 700 : 500,
                      letterSpacing: "0.03em",
                      color: isActive || isCompleted ? "#fff" : "rgba(255,255,255,0.72)",
                      lineHeight: 1.25,
                      maxWidth: 90,
                    }}
                >
                  {label}
                </MDTypography>
              </Box>
          );
        })}
      </Box>
    </Box>
);

const mapPackageFromApi = (tripPackageSummary, detail) => {

  const durationDays = tripPackageSummary.duration_days
  const nights = durationDays ? Math.max(durationDays - 1, 0) : null;
  const services = detail?.components || [];
  const addons = detail?.addons || [];
  const departures = detail?.departure_dates || [];
  const itinerary = detail?.itineraries || [];
  const itineraryDays = (itinerary ? itinerary[0]?.itinerary_days || [] : [])
    .slice()
    .sort((a, b) => (a.day_number ?? 0) - (b.day_number ?? 0));
  console.log("ITINERARY DAY ", itineraryDays);
  console.log("SERVICES ", services);

  // Derive a category for bucketing when there's no linked service to read
  // one from - standalone addons (linked_component_id: null, e.g. "Bungy
  // Jumping") exist purely off addon_type and never point at a service.
  const inferAddonCategory = (addon, linkedService) => {
    if (linkedService?.category) return linkedService.category;
    return addon.type ? addon.type.replace(/_addon$/, "") : null;
  };

  // Map addons with their respective services (may be null for standalone
  // addons) [{addon: {.7..}, linkedService: {...} | null, category}, ...]
  // Only Active add-ons are selectable on an Order (AC1).
  const addons_services = (Array.isArray(addons) ? addons : [])
    .filter((addon) => (addon.status || "").toLowerCase() === "active")
    .map((addon) => {
        const linkedService = services.find((service) => service.id === addon.linked_component_id) || null;
        return { addon, linkedService, category: inferAddonCategory(addon, linkedService) };
  });
    console.log("ADDON SERVICE", addons_services);

  // Services which are linked to addons, implies not are base services.
  const linkedComponentIds = new Set(
    (Array.isArray(addons) ? addons : [])
        .map((addon) => addon.linked_component_id)
        .filter((id) => id != null)
  );

  // Base accommodation, grouped by itinerary day. A day is kept if it has an
  // included accommodation service OR a `specific_day` accommodation addon
  // targeting it — the latter lets us show an upgrade-only day (no included
  // stay yet, but one is offered) under its own heading.
  const specificDayAccommodationDayIds = new Set(
    addons_services
        .filter((item) => item.category === "accommodation" && item.addon.application_scope === "specific_day" && item.addon.itinerary_day_id != null)
        .map((item) => item.addon.itinerary_day_id)
  );

  // Base accommodation, grouped by itinerary day. For each day, resolve
  // every line item's trip_component_map against the service catalog and
  // keep only accommodation services that aren't already claimed by an
  // addon. Days that end up with nothing qualifying are dropped entirely -
  // this also naturally excludes any accommodation service that isn't
  // linked to any itinerary day.
  const baseAccommodationByDay = itineraryDays
    .map((day) => {
        const dayAccommodations = (day.line_items || [])
            .map((lineItem) => services.find((service) => service.id === lineItem.trip_component_id))
            .filter(Boolean)
            .filter((service) => service.category === "accommodation" && !linkedComponentIds.has(service.id))
            .filter((service, index, arr) => arr.findIndex((s) => s.id === service.id) === index);

        return {
            day_id: day.id, // used to match addon.itinerary_day_id
            day_number: day.day_number,
            day_title: day.day_title,
            accommodations: dayAccommodations,
        };
    })
    .filter((day) => day.accommodations.length > 0 || specificDayAccommodationDayIds.has(day.day_id));

  // Same day-grouping treatment for non-accommodation services - a service
  // only counts as "included" if it's actually linked to an itinerary day's
  // line items. Days are kept if they have an included service OR a
  // specific_day, non-accommodation addon targeting them (an upgrade-only
  // day), same reasoning as accommodation.
  const specificDayOtherAddonDayIds = new Set(
    addons_services
        .filter((item) => item.category !== "accommodation" && item.addon.application_scope === "specific_day" && item.addon.itinerary_day_id != null)
        .map((item) => item.addon.itinerary_day_id)
  );

  const baseServicesByDay = itineraryDays
    .map((day) => {
        const dayServices = (day.line_items || [])
            .map((lineItem) => services.find((service) => service.id === lineItem.trip_component_id))
            .filter(Boolean)
            .filter((service) => service.category !== "accommodation" && !linkedComponentIds.has(service.id))
            .filter((service, index, arr) => arr.findIndex((s) => s.id === service.id) === index);

        return {
            day_id: day.id,
            day_number: day.day_number,
            day_title: day.day_title,
            services: dayServices,
        };
    })
    .filter((day) => day.services.length > 0 || specificDayOtherAddonDayIds.has(day.day_id));
    console.log("BASE SERVICE BY DAY", baseServicesByDay);

  const accommodation_addons = addons_services.filter((item) => item.category === "accommodation");
  const other_addons = addons_services.filter((item) => item.category !== "accommodation");
  console.log("OTHER ADDONS", other_addons);

  const linkedTrips = tripPackageSummary.trips
      .map((trip) => {
        return {
          id: trip.id,
          title: trip.title,
          primary_country: trip.primary_country,
          status: trip.status
        };
      })

    return {
        id: tripPackageSummary.id,
        title: tripPackageSummary.title,
        status: tripPackageSummary.status,
        sequence: tripPackageSummary.sequence,
        duration_days: tripPackageSummary.duration_days,
        duration: durationDays ? `${durationDays} Days / ${nights} Nights` : "-",
        base_price: tripPackageSummary.base_price,
        currency: tripPackageSummary.currency,
        created_at: tripPackageSummary.created_at,
        updated_at: tripPackageSummary.updated_at,
        trips: linkedTrips,
        destinations: linkedTrips.map((trip) => trip.primary_country),
        image: tripPackageSummary.banner_image || "",
        // IF detail endpoint brought complete package info.
        description: detail ? detail.description : "",
        cities: detail ? detail.cities : "",
        tags: detail ? detail.tags : "",
        package_tier: detail ? detail.package_tier : "",
        payment_templates: detail ? detail.payment_templates : [],
        components: detail ? detail.components : [],
        departure_dates: detail ? detail.departure_dates : [],
        inclusions: itinerary ? itinerary[0]?.inclusion || "" : "",
        // Evaluated
        base_accommodation: baseAccommodationByDay, // [{ day_number, day_title, accommodations: [...] }]
        base_services: baseServicesByDay,
        accommodation_addons: accommodation_addons,
        other_addons: other_addons,
        services: services,
        payment_template_ids: Array.isArray(detail?.payment_templates) ? detail?.payment_templates : [],
        addons: addons,
        itineraries: itinerary ? itinerary[0] : "",
        faqs: detail?.faqs?.faqs || detail?.faqs || [],
    }
};


function CreateTravelOrder() {
  // The window in classic; <main> in the v2 frame (see shell-v2/ScrollContainer).
  const scroller = useScrollContainer();

  const { axiosInstance } = useAuth();
  const navigate = useNavigate();
  const { id: editOrderId } = useParams();
  const isEditMode = Boolean(editOrderId);

  const [taxes, setTaxes] = useState([]);
  const [originalOrderStatus, setOriginalOrderStatus] = useState(null);
  const [originalOrderSubStatus, setOriginalOrderSubStatus] = useState(null);

  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [stepError, setStepError] = useState("");
  const [orderRecordId, setOrderRecordId] = useState(editOrderId || null);
  const [isSavingStep, setIsSavingStep] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  // The screen must be scrolled to top, whenever user hits the next or back step button.
  useEffect(() => {
    scroller.scrollTo({ top: 0, behavior: "smooth" });
    // Only on step change: `scroller` is a fresh object every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep]);

  // Error handler
  const [fieldErrors, setFieldErrors] = useState({});
  const setFieldError = (fieldKey, message) => {
    setFieldErrors((prev) => {
      if (prev[fieldKey] === message) return prev; // no actual change, skip re-render
      return { ...prev, [fieldKey]: message };
    });
  };

  // Wizard Step - 1 : Select Trip [ State management ]
  const [isLoadingTripPackages, setIsLoadingTripPackages] = useState(false);
  const [tripPackages, setTripPackages] = useState([]);
  const [selectedTripPackage, setSelectedTripPackage] = useState(null);

  const [searchDeparture, setSearchDeparture] = useState("");
  const [travelAfterDate, setTravelAfterDate] = useState(new Date());
  const [travelBeforeDate, setTravelBeforeDate] = useState(null);
  const [selectedDepartureDate, setSelectedDepartureDate] = useState(null);
  const [isLoadingAllocations, setIsLoadingAllocations] = useState(false);
  const [allocationMap, setAllocationMap] = useState({});
  const [hssTemplateMap, setHssTemplateMap] = useState({});
  const [isLoadingHssTemplates, setIsLoadingHssTemplates] = useState(false);

  // Also used in step 2 and 3
  const [travelers, setTravelers] = useState(() => [createEmptyTraveler(true, true)]);

  // Wizard Step - 2 : Traveler Details [ State management ]
  const travelerStepRef = useRef(null);
  const [seatsError, setSeatsError] = useState("");
  const [secureAllocation, setSecureAllocation] = useState(0);
  const [isLoadingSeats, setIsLoadingSeats] = useState(false);

  // Wizard Step - 3 : Trip Customization [ State management ]
  const [selectedAccommodationIds, setSelectedAccommodationIds] = useState([]);
  const [accommodationTravelerMap, setAccommodationTravelerMap] = useState({});
  const [invalidAccommodationIds, setInvalidAccommodationIds] = useState([]);
  const [selectedAddonIds, setSelectedAddonIds] = useState([]);
  const [addonTravelerMap, setAddonTravelerMap] = useState({}); // { [addonId]: [travelerId, ...] }
  const [invalidAddonIds, setInvalidAddonIds] = useState([]);

  // Wizard Step - 4 : Payment & Review [ State management ]
  const [paymentError, setPaymentError] = useState("");
  const [isEditingFromReview, setIsEditingFromReview] = useState(false);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [templatesError, setTemplatesError] = useState("");
  const [paymentTemplates, setPaymentTemplates] = useState([]);
  const [invalidPaymentTemplate, setInvalidPaymentTemplate] = useState(false);
  const [selectedPaymentTemplateId, setSelectedPaymentTemplateId] = useState(null);
  const [selectedGateway, setSelectedGateway] = useState("");
  const [gatewayAvailability, setGatewayAvailability] = useState(null);
  const [isLoadingGateways, setIsLoadingGateways] = useState(false);
  const [gatewayError, setGatewayError] = useState("");
  const [paymentLinkModalOpen, setPaymentLinkModalOpen] = useState(false);
  const [paymentLinkInvoiceId, setPaymentLinkInvoiceId] = useState(null);
  const [paymentLinkDefaultEmail, setPaymentLinkDefaultEmail] = useState("");
  const [bookingStatus, setBookingStatus] = useState("Draft");
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [latestOrderId, setLatestOrderId] = useState("");
  const [createdOrderNumericId, setCreatedOrderNumericId] = useState(null);
  const [successToast, setSuccessToast] = useState("");
  // CHECK THIS : Need to work on this ?
  const [flightPreference, setFlightPreference] = useState("Economy");

  // -------------------------------------------------
  // Order Summary
  // -------------------------------------------------

  const selectedHssTemplate = useMemo(
      () => (selectedDepartureDate?.hss_template ? hssTemplateMap[selectedDepartureDate.hss_template] : null),
      [selectedDepartureDate, hssTemplateMap]
  );


  // -------------------------------------------------
  // Step 1 : Select Trip
  // -------------------------------------------------

  // Retrieving Trip Packages.
  // DO SOMETHING TO MAKE SURE BACKEND CHECKS EVERYTHING TO MAKE SURE ONLY BOOKABLE PACKAGE ARE RETURNED.

  useEffect(() => {
      const loadPackages = async () => {
          setIsLoadingTripPackages(true);
          try {
              const res = await axiosInstance.get('/trip-packages?status=active');
              const packages = Array.isArray(res.data) ? res.data : [];
              if (!packages.length) return;

              const detailed = await Promise.all(
                  packages.map(async (pkg) => {
                      try {
                          const [detailRes, heroImage] = await Promise.all([
                              axiosInstance.get(`/trip-packages/${pkg.id}`),
                              resolvePackageHeroImage(pkg.id),
                          ]);
                          const mapped = mapPackageFromApi(pkg, detailRes.data);
                          return { ...mapped, image: heroImage || mapped.image}
                      } catch {
                          return mapPackageFromApi(pkg, null);
                      }
                  })
              );
              setTripPackages(detailed);
          } catch (err) {
              console.log("Unable to retrieve packages :", err);
          } finally {
              setIsLoadingTripPackages(false);
          }
      };
    loadPackages();
  }, []);

  // Filtered Trip Packages

  const filteredTripPackages = useMemo(() => {
    const q = searchDeparture.trim().toLowerCase();
    const afterTime = travelAfterDate ? new Date(travelAfterDate).getTime() : null;
    const beforeTime = travelBeforeDate ? new Date(travelBeforeDate).getTime() : null;

    return tripPackages.filter((pkg) => {
      // --- text match (name / departure city) ---
      const matchesText =
          !q ||
          pkg.title.toLowerCase().includes(q) ||
          (pkg.trips || []).some((trip) => (trip.primary_country || "").toLowerCase().includes(q)) ||
          (pkg.cities || []).some((city) => city.toLowerCase().includes(q));

      if (!matchesText) return false;

      // --- date range match against departure_dates[].departure_date ---
      if (!afterTime && !beforeTime) return true; // no date filter applied

      const departure_dates = pkg.departure_dates || [];
      if (departure_dates.length === 0) return false; // no departures to check against

      return departure_dates.some((opt) => {
        if (!opt.departure_date) return false;
        const depTime = new Date(opt.departure_date).getTime();
        if (Number.isNaN(depTime)) return false;

        if (afterTime && depTime < afterTime) return false;
        if (beforeTime && depTime > beforeTime) return false;
        return true;
      });
    });
  }, [searchDeparture, tripPackages, travelAfterDate, travelBeforeDate]);

  // When a trip package is selected.

  const handleSelectTripPackage = async (tripPackage) => {
    // if we're mid-edit-from-review and the trip itself is changing
    // (not just re-clicking the same trip), drop the "jump straight back to
    // Review" shortcut. A different trip can change traveler seat limits,
    // customization options, and pricing, so force a normal walkthrough of
    // every remaining step instead of fast-tracking back to a stale Review.
    if (isEditingFromReview && selectedTripPackage?.id !== tripPackage?.id) {
      setIsEditingFromReview(false);
    }
    setSelectedTripPackage(tripPackage);
    setSelectedDepartureDate(null);
    setFieldError("trip", null);
    setFieldError("departureDate", null);
    resetTripCustomization();
  };

  // Clear Package Selection.

  const ClearPackageSelection = () => {
    setSelectedTripPackage(null);
    setSelectedDepartureDate(null);
    resetTripCustomization();
  };

  // Retrieve secured seats available for all the departures date belongs to selected trip package.

  useEffect(() => {
    const departureIds = (selectedTripPackage?.departure_dates || [])
        .map((opt) => opt.id)
        .filter(Boolean);

    if (!departureIds.length) {
        setAllocationMap({});
        return;
    }

    let cancelled = false;
    const loadAllocations = async () => {
        setIsLoadingAllocations(true);
        try {
                const res = await axiosInstance.get(
                    `/travel-orders/secured-allocation-batch?departure_date_ids=${departureIds.join(",")}`
                );
                if (!cancelled) {
                    setAllocationMap(res.data?.allocations || {});
                }
            } catch (err) {
                if (!cancelled) {
                    setAllocationMap({});
                }
            } finally {
                if (!cancelled) setIsLoadingAllocations(false);
            }
        };

        loadAllocations();
        return () => {
            cancelled = true;
        };
    }, [selectedTripPackage?.id]);

  // Handle Departure Date Selection

  const handleChangeDepartureDate = (option) => {
        if (!option) {
            setSelectedDepartureDate(null);
            setFieldError("departureDate", null);
            return;
        }

        // If allocations for this trip are still loading, we don't have a trustworthy
        // seat count yet - refuse to commit the change rather than risk overselling.
        if (isLoadingAllocations) {
            setFieldError("departureDate", "Checking seat availability for this departure date, please try again in a moment.")
            return;
        }

        const requiredSeats = travelers.length;
        const secured = allocationMap[option.id] ?? 0;
        const totalAllocation = option.total_allocation ?? 0;
        const seatsAvailable = Math.max(totalAllocation - secured, 0);

        if (requiredSeats > seatsAvailable) {
            setFieldError(
                "departureDate",
                `You have configured ${requiredSeats} traveler${requiredSeats === 1 ? "" : "s"}, but only ` +
                `${seatsAvailable} seat${seatsAvailable === 1 ? "" : "s"} ${seatsAvailable === 1 ? "is" : "are"} available ` +
                `on this departure date. Please select another departure date or remove a traveler.`
            );
            return;
        } else {
            setFieldError("departureDate", null);
        }

        if (selectedDepartureDate?.id !== option.id) {
            resetTripPackageCustomization(); // each departure date owns its own addon catalog
        }

        setSelectedDepartureDate(option);
        setFieldError("departureDate", false);
    };

  // Departure date has been changes, implies all add-on selection would be cleaned up.

  const resetTripPackageCustomization = useCallback(() => {
    setSelectedAccommodationIds([]);
    setSelectedAddonIds([]);
    setAddonTravelerMap({});
    setAccommodationTravelerMap({});
    setInvalidAddonIds([]);
    setInvalidAccommodationIds([]);
  }, []);

  // Retrieve package hero image url.

  const resolvePackageHeroImage = async (packageId) => {
    try {
        const response = await axiosInstance.get('/attachments/', {
            params: {
                parent_table: 'trip_packages',
                parent_id: packageId
            }
        });
        return await handlePreviewAttachment(response.data[0]);
    } catch (err) {
        console.error(`Error fetching attachments for package ${packageId}:`, err);
        return "";
    }
  };

  // Extracts package hero image.

  const handlePreviewAttachment = async (attachment) => {
        try {
            if (!attachment.bucket_name || !attachment.storage_path) {
                throw new Error('Attachment storage metadata is invalid for strict R2 download');
            }
            return await previewPrivateObject({
                axiosInstance,
                attachmentId: attachment.id,
            });
        } catch (error) {
            console.error('Error previewing attachment:', error);
        }
    };

  // -------------------------------------------------
  // Step 2 : Traveler Details
  // -------------------------------------------------




  // -------------------------------------------------
  // Step 1 : Select Trip
  // -------------------------------------------------

  const fetchTaxes = async () => {
    try {
      const response = await axiosInstance.get('/taxes/active');
      setTaxes(response.data);

    } catch (err) {
      console.error(`Unable to retrieve taxes`, err);
    }
  };

    useEffect(() => {
        const ids = new Set();
        tripPackages.forEach((pkg) => {
            (pkg.departure_dates || []).forEach((dep) => {
                if (dep.hss_template) ids.add(dep.hss_template);
            });
        });
        const idList = Array.from(ids);

        if (!idList.length) {
            setHssTemplateMap({});
            return;
        }

        let cancelled = false;
        const loadHssTemplates = async () => {
            setIsLoadingHssTemplates(true);
            try {
                const res = await axiosInstance.get(`/hss?ids=${idList.join(",")}`);
                const templates = Array.isArray(res.data) ? res.data : [];
                if (!cancelled) {
                    const map = {};
                    templates.forEach((t) => { map[t.id] = t; });
                    setHssTemplateMap(map);
                }
            } catch (err) {
                if (!cancelled) setHssTemplateMap({});
            } finally {
                if (!cancelled) setIsLoadingHssTemplates(false);
            }
        };
        loadHssTemplates();
        return () => { cancelled = true; };
    }, [tripPackages]);

  // Whenever departure after filter applied

  function updateTravelAfterDate(date) {
    if (!date) {
      setFieldError('travelAfterDate', null);
      setFieldError('travelBeforeDate', null);
      setTravelAfterDate(date);
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const enteredDate = new Date(date);
    enteredDate.setHours(0, 0, 0, 0);

    const dateTo = new Date(travelBeforeDate);
    dateTo.setHours(0, 0, 0, 0);

    if (enteredDate < today) {
      setFieldError('travelAfterDate', 'Date can\'t be before today');
    } else if (travelBeforeDate && enteredDate > dateTo) {
      setFieldError('travelAfterDate', 'Date must be before the "To" date');
    }else {
      setFieldError('travelAfterDate', null);
      setFieldError('travelBeforeDate', null);
    }

    setTravelAfterDate(date);
  }

  // Whenever departure before filter applied

  function updateTravelBeforeDate(date) {
    if (!date) {
      setFieldError('travelBeforeDate', null);
      setFieldError('travelAfterDate', null);
      setTravelBeforeDate(date);
      return;
    }

    const dateFrom = new Date(travelAfterDate);
    dateFrom.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const enteredDate = new Date(date);
    enteredDate.setHours(0, 0, 0, 0);

    if (travelBeforeDate && enteredDate < dateFrom) {
      setFieldError('travelBeforeDate', 'Date must be after the "From" date');
    } else if (enteredDate < today) {
        setFieldError('travelBeforeDate', 'Date can\'t be before today');
    } else {
      setFieldError('travelBeforeDate', null);
      setFieldError('travelAfterDate', null);
    }

    setTravelBeforeDate(date);
  }

  // Reset all the data populated by trip customization step

  const resetTripCustomization = useCallback(() => {
    setSelectedAccommodationIds([]);
    setSelectedAddonIds([]);
    setAddonTravelerMap({});
    setAccommodationTravelerMap({});
    setInvalidAddonIds([]);
    setInvalidAccommodationIds([]);
  }, []);

  // -------------------------------------------------
  // Step 2 : Traveler Details
  // -------------------------------------------------

  // Retrieve seats available for all departure date that belongs to the selected trip

  useEffect(() => {
    if (activeStep !== 1 || !selectedDepartureDate?.id) return;

    const depId = selectedDepartureDate.id;
    const cachedValue = allocationMap[depId];
    const hasCachedValue = cachedValue !== undefined;

    if (hasCachedValue) {
      setSecureAllocation(cachedValue);
      setSeatsError("");
    } else {
      setIsLoadingSeats(true);
    }

    let cancelled = false;
    const refreshSecuredCount = async () => {
      try {
        const res = await axiosInstance.get(
            `/travel-orders/secured-allocation?departure_date_id=${depId}`
        );
        if (!cancelled) {
          const fresh = res.data?.secured_allocation ?? 0;
          setSecureAllocation(fresh);
          setAllocationMap((prev) => ({ ...prev, [depId]: fresh }));
          setSeatsError("");
        }
      } catch (err) {
        if (!cancelled && !hasCachedValue) {
          setSeatsError("Failed to load seat availability. Please try again.");
          setSecureAllocation(0);
        }
      } finally {
        if (!cancelled) setIsLoadingSeats(false);
      }
    };

    refreshSecuredCount();
    return () => {
      cancelled = true;
    };
  }, [activeStep, selectedDepartureDate?.id]);

  // Evaluate seats left for the selected departure date.

  const seatsLeft = useMemo(() => {
    const totalAllocation = selectedDepartureDate?.total_allocation ?? 0;
    return Math.max(totalAllocation - secureAllocation, 0);
  }, [selectedDepartureDate, secureAllocation]);

  // -------------------------------------------------
  // Step 3 : Trip Customization
  // -------------------------------------------------

  // Handle Accommodation selection

  const handleAccommodationToggle = (accommodationId) => {
    setSelectedAccommodationIds((prev) => {
      const isRemoving = prev.includes(accommodationId);
      if (isRemoving) {
        setAccommodationTravelerMap((map) => {
          const { [accommodationId]: _removed, ...rest } = map;
          return rest;
        });
        setInvalidAccommodationIds((ids) => ids.filter((id) => id !== accommodationId));
        return prev.filter((id) => id !== accommodationId);
      }
      return [...prev, accommodationId];
    });
  };

  // Handle addition removal of traveler to the selected accommodation

  const handleAccommodationTravelersChange = (accommodationId, travelerIds) => {
    setAccommodationTravelerMap((prev) => ({ ...prev, [accommodationId]: travelerIds }));
    if (travelerIds.length > 0) {
      setInvalidAccommodationIds((prev) => prev.filter((id) => id !== accommodationId));
    }
  };

  // Handle Add-ons selection

  const handleAddonToggle = (addonId) => {
    setSelectedAddonIds((prev) => {
      const isRemoving = prev.includes(addonId);
      if (isRemoving) {
        setAddonTravelerMap((map) => {
          const { [addonId]: _removed, ...rest } = map;
          return rest;
        });
        setInvalidAddonIds((prev) => prev.filter((id) => id !== addonId));
        return prev.filter((id) => id !== addonId);
      }
      return [...prev, addonId];
    });
  };

  const travelerCount = travelers.length;
    const tripNights = useMemo(
        () => getTripNights(selectedDepartureDate?.departure_date, selectedDepartureDate?.return_date, selectedTripPackage?.duration),
        [selectedDepartureDate, selectedTripPackage]
    );

  // Handle addition/removal of traveler to the selected add-on

  const handleAddonTravelersChange = (addonId, travelerIds) => {
    setAddonTravelerMap((prev) => ({ ...prev, [addonId]: travelerIds }));
    if (travelerIds.length > 0) {
      setInvalidAddonIds((prev) => prev.filter((id) => id !== addonId)); // NEW
    }
  };

  // -------------------------------------------------
  // Step 4 : Review & Payment
  // -------------------------------------------------

  // Helper func to switch to editing certain step

  const handleEditFromReview = (stepIndex) => {
    setIsEditingFromReview(true);
    setActiveStep(stepIndex);
    setStepError("");
  };

  // Retrieve active payment templates for the selected trip

    useEffect(() => {
        if (!selectedTripPackage?.id) {
            setPaymentTemplates([]);
            setSelectedPaymentTemplateId(null);
            return;
        }
        let cancelled = false;
        const loadTemplates = async () => {
            setIsLoadingTemplates(true);
            setTemplatesError("");
            try {
                const res = await axiosInstance.get(
                    `/payment-templates?trip_package_id=${selectedTripPackage.id}&status=active`
                );
                const templates = Array.isArray(res.data) ? res.data : [];
                const allowedIds = selectedTripPackage.payment_template_ids || [];
                const filtered = allowedIds.length
                    ? templates.filter((t) => allowedIds.includes(t.id))
                    : templates;

                if (!cancelled) {
                    setPaymentTemplates(filtered);
                    setSelectedPaymentTemplateId((prev) => {
                        if (prev && filtered.some((t) => t.id === prev)) return prev;
                        return filtered.length === 1 ? filtered[0].id : null;
                    });
                }
            } catch (err) {
                if (!cancelled) {
                    setTemplatesError("Failed to load payment plans for this package.");
                    setPaymentTemplates([]);
                }
            } finally {
                if (!cancelled) setIsLoadingTemplates(false);
            }
        };
        loadTemplates();
        fetchTaxes();
        return () => { cancelled = true; };
    }, [selectedTripPackage?.id]);

  // Handle payment template selection

  const handleSelectPaymentTemplate = (templateId) => {
    setSelectedPaymentTemplateId(templateId);
    setInvalidPaymentTemplate(false);
  };

  // Retrieve selected accommodation upgrades.

    const selectedAccommodations = useMemo(
        () =>
            (selectedTripPackage?.accommodation_addons || []).filter((item) =>
                selectedAccommodationIds.includes(item.addon.id)
            ),
        [selectedTripPackage, selectedAccommodationIds]
    );

  // Retrieve selected add-ons upgrades.

    const selectedAddons = useMemo(
        () => (selectedTripPackage?.other_addons || []).filter((item) => selectedAddonIds.includes(item?.addon?.id)),
        [selectedTripPackage, selectedAddonIds]
    );

  // Retrieve selected payment template

  const selectedPaymentTemplate = useMemo(
      () => paymentTemplates.find((t) => t.id === selectedPaymentTemplateId) || null,
      [paymentTemplates, selectedPaymentTemplateId]
  );

  // Retrieve pricing

    const reviewPricing = useMemo(() => {
        if (!selectedTripPackage) {
            return { subtotal: 0, accommodationAmount: 0, addonAmount: 0, hssSurchargeAmount: 0, isHssLive: false, total: 0, amountPayable: 0, remainingAmount: 0, paymentSchedule: [] };
        }
        return buildReviewPricing({
            baseSubtotal: selectedTripPackage.base_price * travelerCount,
            selectedAccommodations,
            accommodationTravelerMap,
            selectedAddons,
            addonTravelerMap,
            tripNights,
            paymentTemplate: selectedPaymentTemplate,
            selectedDepartureDate,
            hssTemplate: selectedHssTemplate,
            taxes,
        });
    }, [selectedTripPackage, travelerCount, selectedAccommodations, accommodationTravelerMap, selectedAddons, addonTravelerMap, tripNights, selectedPaymentTemplate, selectedDepartureDate, selectedHssTemplate, taxes]);

  // Load enabled payment gateways on the review/payment step (AC1 / AC10).

  useEffect(() => {
    if (activeStep !== 3) return;
    let cancelled = false;
    const loadGateways = async () => {
      setIsLoadingGateways(true);
      setGatewayError("");
      try {
        const res = await axiosInstance.get("/payment-gateways/availability");
        if (cancelled) return;
        setGatewayAvailability(res.data);
        const { stripe, razorpay } = res.data || {};
        if (stripe && !razorpay) {
          setSelectedGateway("stripe");
        } else if (razorpay && !stripe) {
          setSelectedGateway("razorpay");
        } else if (!stripe && !razorpay) {
          setSelectedGateway("");
        }
      } catch (err) {
        if (!cancelled) {
          setGatewayError(
            err?.response?.data?.detail || "Unable to load payment gateways."
          );
        }
      } finally {
        if (!cancelled) setIsLoadingGateways(false);
      }
    };
    loadGateways();
    return () => {
      cancelled = true;
    };
  }, [activeStep, axiosInstance]);

  // Handle payment on last step — create travel order, then open email popup with payment link.

  const handlePayNow = async () => {
    if (!selectedPaymentTemplateId) {
      setInvalidPaymentTemplate(true);
      setPaymentError("Please select a payment plan to continue.");
      return;
    }
    if (!selectedGateway) {
      setPaymentError("Please select Stripe or Razorpay to continue.");
      return;
    }
    setInvalidPaymentTemplate(false);
    setPaymentError("");
    setIsProcessingPayment(true);
    setBookingStatus("Payment Pending");

    try {
      // Reuse the same order on retry (network / pending-payment failure) — never
      // create a duplicate booking when orderRecordId is already known.
      const created = await submitWizardOrder({
        // Stay Draft until Deposit/Full Payment is actually paid. Secured is
        // set by payment sync, which also creates MOLs + merchant emails.
        status: "Draft",
        subStatus: "Payment Pending",
      });
      setLatestOrderId(created.order_id);
      setCreatedOrderNumericId(created.id);

      const pendingRes = await axiosInstance.get(
        `/payment-gateways/travel-orders/${created.id}/pending-payment`
      );
      if (!pendingRes.data?.invoice_id) {
        throw new Error(
          "No payable invoice was created for this booking. Check the payment plan and try again."
        );
      }

      const primary = travelers.find((t) => t.isPrimary) || travelers[0];
      setPaymentLinkDefaultEmail(primary?.email || "");
      setPaymentLinkInvoiceId(pendingRes.data.invoice_id);
      setPaymentLinkModalOpen(true);
    } catch (err) {
      const message =
          err?.response?.data?.detail ||
          err?.message ||
          "Unable to prepare payment link. Please try again.";
      setPaymentError(typeof message === "string" ? message : JSON.stringify(message));
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handlePaymentLinkSent = () => {
    setPaymentLinkModalOpen(false);
    setCompletedSteps((prev) => new Set([...prev, 3]));
    setActiveStep(4);
    setSnackbar({
      open: true,
      message: `Payment link sent — order ${latestOrderId || createdOrderNumericId || ""}.`,
      severity: 'success'
    });
  };

  const handleResendPaymentLink = async () => {
    const orderId = createdOrderNumericId || orderRecordId;
    if (!orderId) {
      setPaymentError("Order is not ready yet. Go back and send the payment link again.");
      setActiveStep(3);
      return;
    }
    setIsProcessingPayment(true);
    setPaymentError("");
    try {
      const pendingRes = await axiosInstance.get(
        `/payment-gateways/travel-orders/${orderId}/pending-payment`
      );
      if (!pendingRes.data?.invoice_id) {
        throw new Error("No pending payment found for this booking.");
      }
      const primary = travelers.find((t) => t.isPrimary) || travelers[0];
      setPaymentLinkDefaultEmail(primary?.email || "");
      setPaymentLinkInvoiceId(pendingRes.data.invoice_id);
      setPaymentLinkModalOpen(true);
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.message ||
        "Unable to open payment link. Please try again.";
      setSnackbar({
        open: true,
        message: typeof message === "string" ? message : JSON.stringify(message),
        severity: "error",
      });
    } finally {
      setIsProcessingPayment(false);
    }
  };

  // -------------------------------------------------
  // Editing Existing Order
  // -------------------------------------------------

  const [isLoadingOrderForEdit, setIsLoadingOrderForEdit] = useState(isEditMode);

    useEffect(() => {
        if (!isEditMode || !tripPackages.length) return;
        let cancelled = false;

        const loadOrderForEdit = async () => {
            setIsLoadingOrderForEdit(true);
            try {
                const res = await axiosInstance.get(`/travel-orders/${editOrderId}/edit`);
                const data = res.data;
                if (cancelled) return;

                const tripPackage = tripPackages.find((p) => p.id === data.trip_package_id);

                if (!tripPackage) {
                    setStepError("Could not find the trip package associated with this order. It may have been removed.");
                    return;
                }

                setSelectedTripPackage(tripPackage);

                const departureOption =
                    (tripPackage.departure_dates || []).find((d) => d.id === data.departure_date_id) || null;
                setSelectedDepartureDate(departureOption);

        // Build travelers, tracking server id -> client id so addon refs can be remapped below
        const serverIdToClientId = {};
        const hydratedTravelers = (data.travelers || []).map((t) => {
          const clientId = `existing-${t.id}`;
          serverIdToClientId[t.id] = clientId;
          return {
            ...createEmptyTraveler(Boolean(t.is_primary), true),
            id: clientId,
            server_id: t.id,
            first_name: t.first_name || "",
            middle_name: t.middle_name || "",
            last_name: t.last_name || "",
            email: t.email || "",
            phoneCountryCode: t.dial_code || "",
            phone: t.mobile || "",
            dateOfBirth: t.date_of_birth ? new Date(t.date_of_birth) : null,
            gender: t.gender || "",
            nationality: t.nationality || "",
            address: t.address || "",
            country: t.country || "",
            passportNumber: t.passport_number || "",
            passportExpiry: t.passport_expiry_date ? new Date(t.passport_expiry_date) : null,
            passportLocation: t.passport_location || "",
            passportIssueDate: t.passport_issue_date ? new Date(t.passport_issue_date) : null,
            specialDetails: t.special_request || "",
            isPrimary: Boolean(t.is_primary),
          };
        });
        setTravelers(hydratedTravelers);

        // Addons — remap traveler_client_refs (server pax ids) -> our new client ids
        const accommodationIds = [];
        const addonIds = [];
        const accMap = {};
        const addonMap = {};
        (data.addons || []).forEach((a) => {
          const refs = (a.traveler_client_refs || [])
              .map((serverId) => serverIdToClientId[serverId])
              .filter(Boolean);
          if (a.category === "accommodation") {
            accommodationIds.push(a.addon_id);
            accMap[a.addon_id] = refs;
          } else {
            addonIds.push(a.addon_id);
            addonMap[a.addon_id] = refs;
          }
        });
        setSelectedAccommodationIds(accommodationIds);
        setSelectedAddonIds(addonIds);
        setAccommodationTravelerMap(accMap);
        setAddonTravelerMap(addonMap);

        // Hydrate gateway selection for edit mode using the saved order payment method.
        // Legacy orders may only have payment_method (Card/UPI) without payment_gateway.
        const method = String(data.payment_method || "").toLowerCase();
        const gateway = String(data.payment_gateway || "").toLowerCase();
        if (gateway === "stripe" || gateway === "razorpay") {
          setSelectedGateway(gateway);
        } else if (method === "upi") {
          setSelectedGateway("razorpay");
        } else if (method === "card") {
          setSelectedGateway("stripe");
        }
        setSelectedPaymentTemplateId(data.payment_template_id || null);

        // Preserve original status/sub_status — the Save handler sends these back
        // unchanged so editing an order never silently re-triggers "payment taken"
        setOriginalOrderStatus(data.status);
        setOriginalOrderSubStatus(data.sub_status);

        setCompletedSteps(new Set([0, 1, 2]));
      } catch (err) {
        setStepError("Failed to load order details for editing. Please try again.");
      } finally {
        if (!cancelled) setIsLoadingOrderForEdit(false);
      }
    };

    loadOrderForEdit();
    return () => {
      cancelled = true;
    };
}, [isEditMode, editOrderId, tripPackages]);

  // -------------------------------------------------
  // Stepper
  // -------------------------------------------------

  const handleStepClick = (index) => {
    // While editing an existing order, allow direct jump to any stage.
    if (isEditMode || index <= activeStep || completedSteps.has(index)) {
      setActiveStep(index);
      setStepError("");
    }
  };

  const buildWizardPayload = useCallback(
      ({ status, subStatus }) => {
        const primary = travelers.find((t) => t.isPrimary) || travelers[0];
        const allSelectedAddonItems = [...selectedAccommodations, ...selectedAddons];
        const addonsPayload = allSelectedAddonItems.map((item) => {
          const isAccommodation = item.linkedService?.category === "accommodation";
          const travelerMap = isAccommodation ? accommodationTravelerMap : addonTravelerMap;
          const assignedTravelerClientRefs = travelerMap[item.addon.id] || [];
          return {
            addon_id: item.addon.id,
            service_id: item.linkedService?.id ?? null,
            category: item.linkedService?.category ?? item.category ?? null,
            traveler_client_refs: assignedTravelerClientRefs,
          };
        });

        const basePrice = selectedTripPackage.base_price;
        const offerWindow = getOfferWindow(selectedDepartureDate);
        const rawDiscountPercentage = selectedDepartureDate?.discount_percentage || 0;
        const isDiscountLive = rawDiscountPercentage > 0 && (!offerWindow || offerWindow.isActive);
        const discountPercentage = isDiscountLive ? rawDiscountPercentage : 0;
        const discountedPrice = basePrice - (basePrice * discountPercentage) / 100;

        return {
          trip_package_id: selectedTripPackage.id,
          departure_date_id: selectedDepartureDate?.id,
          channel: "Manual",
          payment_method: GATEWAY_PAYMENT_METHOD[selectedGateway] || "Card",
          order_payment_type: "Deposit",
          payment_template_id: selectedPaymentTemplateId,
          primary_contact_email: primary?.email,
          primary_contact_phone: primary?.phone,
          order_total: reviewPricing.total,
          deposit_amount: reviewPricing.amountPayable,
          pending_amount: reviewPricing.remainingAmount,
          status: status || "Draft",
          sub_status: subStatus || "Draft",
          no_of_installments:
            selectedPaymentTemplate?.payment_type === "deposit_installments"
              ? Math.max(
                  0,
                  (reviewPricing.paymentSchedule || []).filter((row) =>
                    String(row.label || "").startsWith("Installment")
                  ).length
                )
              : null,
          unit_price: discountedPrice,
          travelers: travelers.map((t) => ({
            id: t.server_id || undefined,
            client_ref: t.id,
            first_name: t.first_name,
            middle_name: t.middle_name,
            last_name: t.last_name,
            email: t.email || null,
            dial_code: t.phoneCountryCode || null,
            mobile: t.phone || null,
            date_of_birth: t.dateOfBirth
                ? new Date(t.dateOfBirth).toISOString().slice(0, 10)
                : null,
            gender: t.gender || null,
            nationality: t.nationality || null,
            address: t.address || null,
            country: t.country || null,
            passport_number: t.passportNumber || null,
            passport_expiry_date: t.passportExpiry
                ? new Date(t.passportExpiry).toISOString().slice(0, 10)
                : null,
            passport_location: t.passportLocation || null,
            passport_issue_date: t.passportIssueDate
                ? new Date(t.passportIssueDate).toISOString().slice(0, 10)
                : null,
            special_request: t.specialDetails || null,
            is_primary: Boolean(t.isPrimary),
            departure_city: selectedDepartureDate?.departure_city || null,
          })),
          addons: addonsPayload,
        };
      },
      [
        travelers,
        selectedTripPackage,
        selectedDepartureDate,
        selectedGateway,
        selectedPaymentTemplateId,
        reviewPricing,
        selectedAccommodations,
        selectedAddons,
        accommodationTravelerMap,
        addonTravelerMap,
      ]
  );

  const submitWizardOrder = async ({ status, subStatus }) => {
    if (!selectedDepartureDate?.id) {
      throw new Error(
          "Please select a departure date before continuing."
      );
    }
    const payload = buildWizardPayload({ status, subStatus });
    const response = orderRecordId
        ? await axiosInstance.put(`/travel-orders/wizard/${orderRecordId}`, payload)
        : await axiosInstance.post("/travel-orders/wizard", payload);

    // First successful POST establishes the id for every later save in this session
    if (!orderRecordId && response.data?.id) {
      setOrderRecordId(response.data.id);
    }

    return response.data;
  };

  const createOrderAndExit = async () => {
    try {
      setIsProcessingPayment(true);
      const created = await submitWizardOrder({
        status: "Draft",
        subStatus: "Draft",
      });
      setLatestOrderId(created.order_id);
      setCreatedOrderNumericId(created.id);
      setSnackbar({
        open: true,
        message: `Order successfully created - order id - ${created.order_id}.`,
        severity: 'success'
      });
      setIsEditingFromReview(false);
      setTimeout(() => {
        navigate(`/travel-order/${created.id}`);
      }, 800);
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to create travel order.";
      setStepError(typeof message === "string" ? message : JSON.stringify(message));
    } finally {
      setIsProcessingPayment(false);
    }
  };

  // Scroll to the element.

  const scrollToField = (fieldId) => {
    const el = document.getElementById(fieldId);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
  };


  const validateCurrentStep = () => {

    // Validating step 1

    if (activeStep === 0) {

      // Validating that a trip is selected.

      const tripPackageMissing = !selectedTripPackage;
      if (tripPackageMissing) {
        setFieldError("trip", "Please select a trip package to continue");
        scrollToField("select-trip-section");
        return false;
      } else {
        setFieldError("trip", null);
      }

      // Validating that departure date has been selected.

      const departureMissing = !selectedDepartureDate;
      if (departureMissing) {
        setFieldError("departureDate", "Please select a departure date to continue.");
        scrollToField("departure-date-section");
        return false;
      } else {
        setFieldError("departureDate", null);
      }

      // Validating that the departure date must have enough seats to fill in the configured traveler from step 2.

      const secured = allocationMap[selectedDepartureDate.id] ?? 0;
      const totalAllocation = selectedDepartureDate.total_allocation ?? 0;
      const seatsAvailable = Math.max(totalAllocation - secured, 0);
      if (travelers.length > seatsAvailable) {
        setFieldError(
            "departureDate",
            `You have configured ${travelers.length} traveler${travelers.length === 1 ? "" : "s"}, but this departure ` +
            `date only has ${seatsAvailable} seat${seatsAvailable === 1 ? "" : "s"} left. Please select another departure date.`
        );
        scrollToField("departure-date-section");
        return false;
      } else {
        setFieldError("departureDate", null);
      }
    }

    // Validating step 2 - trigger internal validate func

    if (activeStep === 1) {
      const result = travelerStepRef.current?.validate();
      if (result && !result.isValid) {
        setStepError(result.message);
        if (result.firstErrorFieldDomId) {
          // slight delay so the accordion can finish expanding before we scroll
          setTimeout(() => scrollToField(result.firstErrorFieldDomId), 100);
        }
        return false;
      }
    }

    // Validating step 3 - Trip Customization

    if (activeStep === 2) {

      // At least one traveler must be selected if adding any accommodation add-ons.

      const missingAccommodation = selectedAccommodationIds.filter(
          (id) => !(accommodationTravelerMap[id] && accommodationTravelerMap[id].length > 0)
      );
      if (missingAccommodation.length > 0) {
        setInvalidAccommodationIds(missingAccommodation);
        setStepError("Please select at least one traveler for each selected accommodation.");
        setTimeout(() => scrollToField(`accommodation-travelers-${missingAccommodation[0]}`), 100);
        return false;
      }

      setInvalidAccommodationIds([]);

      // At least one traveler must be selected if adding any add-ons & experiences.

      const missingAddon = selectedAddonIds.filter((id) => {
        const travelerIds = addonTravelerMap[id] || [];
        return travelerIds.length < 1;
      });

      if (missingAddon.length > 0) {
        setInvalidAddonIds(missingAddon);
        setStepError("Please select at least one traveler for each selected add-on.");
        setTimeout(() => scrollToField(`addon-travelers-${missingAddon[0]}`), 100);
        return false;
      }

      // Enforce min/max quantity (Min Pax / Max Pax) from the master add-on
      const paxInvalid = selectedAddonIds.filter((id) => {
        const item = (selectedTripPackage?.other_addons || []).find((x) => x.addon?.id === id);
        if (!item) return false;
        const count = (addonTravelerMap[id] || []).length;
        const minQ = item.addon.min_quantity;
        const maxQ = item.addon.max_quantity;
        if (minQ != null && count < Number(minQ)) return true;
        if (maxQ != null && count > Number(maxQ)) return true;
        return false;
      });
      if (paxInvalid.length > 0) {
        setInvalidAddonIds(paxInvalid);
        const item = (selectedTripPackage?.other_addons || []).find((x) => x.addon?.id === paxInvalid[0]);
        const minQ = item?.addon?.min_quantity;
        const maxQ = item?.addon?.max_quantity;
        setStepError(
          `Traveler count for "${item?.addon?.name || "add-on"}" must be between ${minQ ?? 1} and ${maxQ ?? "∞"}.`
        );
        setTimeout(() => scrollToField(`addon-travelers-${paxInvalid[0]}`), 100);
        return false;
      }

      setInvalidAddonIds([]);
    }

    setStepError("");
    return true;
  };

  const handleNext = async () => {
    if (!validateCurrentStep()) return;

    // edit step 0/1/2 and coming forward again.
    if (activeStep === 2) {
      setIsSavingStep(true);
      try {
        const saved = await submitWizardOrder({
          // If this is an existing Secured order being edited, don't
          // silently downgrade it back to Draft - preserve its status.
          status: originalOrderStatus || "Draft",
          subStatus: originalOrderSubStatus || "Draft",
        });
        setLatestOrderId(saved.order_id);
        setCreatedOrderNumericId(saved.id);
      } catch (err) {
        const message =
            err?.response?.data?.detail ||
            err?.message ||
            "Failed to save trip details. Please try again.";
        setStepError(typeof message === "string" ? message : JSON.stringify(message));
        setIsSavingStep(false);
        return; // don't advance if the save failed
      }
      setIsSavingStep(false);
    }

    if (isEditingFromReview) {
      // send the user back to Review instead of saving immediately —
      // they should see the updated summary before confirming payment
      setCompletedSteps((prev) => new Set([...prev, activeStep]));
      setActiveStep(3);
      setIsEditingFromReview(false);
      setStepError("");
      return;
    }

    setCompletedSteps((prev) => new Set([...prev, activeStep]));
    if (activeStep < WIZARD_STEPS.length - 1) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) setActiveStep((prev) => prev - 1);
  };

  const handleSaveAsDraft = async () => {
    setStepError("");
    await createOrderAndExit();
  };

  const handleSaveEdit = async () => {
    setIsProcessingPayment(true);
    setPaymentError("");
    try {
      const updated = await submitWizardOrder({
        status: originalOrderStatus,
        subStatus: originalOrderSubStatus,
      });
      setSnackbar({
        open: true,
        message: `Order ${updated.order_id} updated successfully.`,
        severity: 'success'
      });
      navigate(`/travel-order/${editOrderId}`);   // fixed: plural, matches your route
    } catch (err) {
      const message = err?.response?.data?.detail || err?.message || "Failed to update travel order.";
      setPaymentError(typeof message === "string" ? message : JSON.stringify(message));
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handleCancelBooking = () => {
    const shouldCancel = window.confirm(
      "Are you sure you want to cancel this booking process? Unsaved progress will be lost."
    );
    if (!shouldCancel) return;
    navigate("/travel-orders");
  };

  const nextLabel =
      isEditingFromReview
          ? "Back to Review"
          : activeStep === 0
              ? "Next: Traveler Details"
              : activeStep === 1
                  ? "Next: Trip Customization"
                  : activeStep === 2
                      ? "Next: Review & Payment"
                      : activeStep === WIZARD_STEPS.length - 1
                          ? "Return to Orders"
                          : `Next: ${WIZARD_STEPS[activeStep + 1]}`;

  const orderNumericId = createdOrderNumericId || orderRecordId;
  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
            <SelectTripStep
                isLoadingTripPackages={isLoadingTripPackages}
                isLoadingOrderForEdit={isLoadingOrderForEdit}
                filteredTripPackages={filteredTripPackages}
                // Filter related params
                searchDeparture={searchDeparture}
                onSearchDepartureChange={setSearchDeparture}
                travelAfterDate={travelAfterDate}
                onTravelAfterDateChange={updateTravelAfterDate}
                travelBeforeDate={travelBeforeDate}
                onTravelBeforeDateChange={updateTravelBeforeDate}
                // Trip Package Selection
                selectedTripPackage={selectedTripPackage}
                onSelectTripPackage={handleSelectTripPackage}
                ClearPackageSelection={ClearPackageSelection}
                // Departure Date Selection
                selectedDepartureDate={selectedDepartureDate}
                allocationMap={allocationMap}
                onChangeDepartureDate={handleChangeDepartureDate}
                isLoadingAllocations={isLoadingAllocations}
                hssTemplateMap={hssTemplateMap}
                fieldErrors={fieldErrors}
            />
        );
      case 1:
        return (
            <TravelerDetailsStep
                ref={travelerStepRef}
                seatsError={seatsError}
                travelers={travelers}
                departure={selectedDepartureDate}
                onTravelersChange={setTravelers}
                maxTravelers={seatsLeft}
                isLoadingSeats={isLoadingSeats}
            />
        );
      case 2:
        return (
          <TripCustomizationStep
              currency={selectedTripPackage.currency}
              baseAccommodation={selectedTripPackage.base_accommodation}
              accommodations={selectedTripPackage.accommodation_addons}
              selectedAccommodationIds={selectedAccommodationIds}
              accommodationTravelerMap={accommodationTravelerMap}
              travelers={travelers}
              invalidAccommodationIds={invalidAccommodationIds}
              onAccommodationToggle={handleAccommodationToggle}
              onAccommodationTravelersChange={handleAccommodationTravelersChange}
              baseAddons={selectedTripPackage.base_services}
              addons={selectedTripPackage.other_addons}
              selectedAddonIds={selectedAddonIds}
              invalidAddonIds={invalidAddonIds}
              addonTravelerMap={addonTravelerMap}
              onAddonToggle={handleAddonToggle}
              nights={tripNights}
              onAddonTravelersChange={handleAddonTravelersChange}
          />
        );
      case 3:
        return (
            <ReviewPaymentStep
                selectedTripPackage={selectedTripPackage}
                selectedDepartureDate={selectedDepartureDate}
                paymentError={paymentError}
                onEditStep={handleEditFromReview}
                flightPreference={flightPreference}
                travelers={travelers}
                selectedAccommodations={selectedAccommodations}
                accommodationTravelerMap={accommodationTravelerMap}
                selectedAddons={selectedAddons}
                addonTravelerMap={addonTravelerMap}
                isLoadingTemplates={isLoadingTemplates}
                templatesError={templatesError}
                paymentTemplates={paymentTemplates}
                invalidPaymentTemplate={invalidPaymentTemplate}
                selectedPaymentTemplateId={selectedPaymentTemplateId}
                onSelectPaymentTemplate={handleSelectPaymentTemplate}
                gatewayAvailability={gatewayAvailability}
                isLoadingGateways={isLoadingGateways}
                gatewayError={gatewayError}
                selectedGateway={selectedGateway}
                onGatewayChange={setSelectedGateway}
                pricing={reviewPricing}
                bookingStatus={bookingStatus}
                isProcessingPayment={isProcessingPayment}
                onPayNow={handlePayNow}
            />
        );
      case 4:
        return (
          <MDBox py={4} textAlign="center">
            <MDTypography variant="h4" fontWeight="bold" color="success" mb={1}>
              Payment Link Sent
            </MDTypography>
            <MDTypography variant="body2" color="text" mb={0.5}>
              The payment link has been sent to the customer. The booking will be confirmed once payment is received.
            </MDTypography>
              <MDTypography variant="button" fontWeight="medium" color="dark" display="block" mb={2}>
                  Order ID:{" "}
                  {orderNumericId ? (
                      <MDTypography
                          component={RouterLink}
                          to={`/travel-order/${orderNumericId}`}
                          variant="button"
                          fontWeight="bold"
                          color="info"
                          sx={{ textDecoration: "underline", cursor: "pointer" }}
                      >
                          {latestOrderId || `#${orderNumericId}`}
                      </MDTypography>
                  ) : (
                      "-"
                  )}
              </MDTypography>
            <MDButton
              variant="outlined"
              color="info"
              onClick={handleResendPaymentLink}
              disabled={isProcessingPayment}
            >
              {isProcessingPayment ? "Preparing…" : "Resend payment link"}
            </MDButton>
          </MDBox>
        );
      default:
        return null;
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SendPaymentLinkModal
        open={paymentLinkModalOpen}
        onClose={() => setPaymentLinkModalOpen(false)}
        invoiceId={paymentLinkInvoiceId}
        defaultEmail={paymentLinkDefaultEmail}
        onSent={handlePaymentLinkSent}
        updateSnackbar={setSnackbar}
      />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                sx={{ overflow: "visible" }}
              >
                <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
                  <MDBox>
                    <MDTypography variant="h5" color="white">
                      {isEditMode ? "Edit Trip Booking" : "New Trip Booking"}   {/* CHANGED — was static "Create Trip Booking" */}
                    </MDTypography>
                    <MDTypography variant="caption" color="white" opacity={0.9} display="block" mt={0.5}>
                      Step {activeStep + 1} of {WIZARD_STEPS.length} — {WIZARD_STEPS[activeStep]}
                    </MDTypography>
                  </MDBox>
                  <MDBox display="flex" gap={1}>
                    <MDButton variant="outlined" color="white" size="small" onClick={handleCancelBooking}>
                      Cancel
                    </MDButton>
                  </MDBox>
                </MDBox>

                <OrderWizardStepper
                  steps={WIZARD_STEPS}
                  activeStep={activeStep}
                  completedSteps={completedSteps}
                  onStepClick={handleStepClick}
                  allStepsClickable={isEditMode}
                />
              </MDBox>

              <MDBox p={3}>
                {stepError && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {stepError}
                  </Alert>
                )}
                {renderStepContent()}

                <MDBox pt={3} sx={{borderTop: "1px solid", borderColor: "divider"}} display="flex" justifyContent="space-between" mt={3}>
                  <MDButton sx={{visibility: activeStep > 0 ? 'visible' : 'hidden'}} variant="outlined" color="dark" onClick={handleBack} disabled={activeStep === 0}>
                    Back
                  </MDButton>
                  {activeStep < 3 ? (
                      <MDButton
                          variant="gradient"
                          color="info"
                          onClick={handleNext}
                          endIcon={<ArrowForwardIcon />}
                          disabled={isProcessingPayment || isSavingStep}
                      >
                        {isSavingStep ? "Saving..." : nextLabel}
                      </MDButton>
                  ) : activeStep === 4 ? (
                    <MDButton
                      variant="gradient"
                      color="info"
                      onClick={() =>
                        navigate(
                          createdOrderNumericId
                            ? `/travel-order/${createdOrderNumericId}`
                            : "/travel-orders"
                        )
                      }
                      onClick={() =>
                          navigate(orderNumericId ? `/travel-order/${orderNumericId}` : "/travel-orders")
                      }
                    >
                        {orderNumericId ? "View Order" : "Return to Orders"}
                    </MDButton>
                  ) : (
                    <MDBox />
                  )}
                </MDBox>
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12} lg={4}>
            <OrderSummaryPanel
                selectedTripPackage={selectedTripPackage}
                travellers={travelerCount}
                selectedDepartureDate={selectedDepartureDate}
                selectedAccommodations={selectedAccommodations}
                accommodationTravelerMap={accommodationTravelerMap}
                selectedAddons={selectedAddons}
                addonTravelerMap={addonTravelerMap}
                tripNights={tripNights}
                pricing={reviewPricing}
                hssTemplate={selectedHssTemplate}
                taxes={taxes}
            />
          </Grid>
        </Grid>
      </MDBox>
      <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default CreateTravelOrder;
